import { Fragment, useState, type ReactNode } from "react";
import type {
  ExitQuoteReasonCode,
  MonitorDetail,
  Operation,
  PositionEarnings,
  PreviewAmount,
  RateQuality,
  RuntimeStatus,
} from "@auto-pools/core";
import { PriceScale } from "./PriceScale";
import { frameScale } from "../lib/rangeScale";
import { TokenPair, TokenPairName } from "./TokenPair";
import { DexBadge } from "./DexBadge";
import { dexName, dexPoolUrl } from "../lib/dex";
import { priceFrame } from "../lib/priceFrame";
import { operationPresentation, ROUTE_PROVIDER_NAMES } from "../lib/operationPresentation";
import { OperationStatus } from "./OperationStatus";
import { useViewer } from "../auth/authContext";
import { PositionPnl } from "./PositionPnl";
import { stopPolicyText } from "../lib/stopPolicy";
import {
  formatAgo,
  formatWhen,
  formatMoney,
  formatPercent,
  formatPercent2,
  formatPercent3,
  formatPrice,
  formatRangeLabel,
  formatSignedAmount,
  formatSignedPercent,
  formatSignedSolShort,
  formatSolShort,
  formatSpan,
  formatToken,
  formatUsd,
  shorten,
  signTone,
} from "../lib/format";
import type { Position } from "../types";

/** The canonical SOL mint; rent and the entry-swap input are always denominated in it. */
const SOL_MINT = "So11111111111111111111111111111111111111112";

const monitorPresentation: Record<string, { tone: string; text: string }> = {
  healthy: { tone: "good", text: "healthy" },
  degraded: { tone: "warn", text: "degraded" },
  stale: { tone: "warn", text: "stale" },
  unavailable: { tone: "bad", text: "unavailable" },
};

const monitorReasons: Record<MonitorDetail["reason"], string> = {
  NO_ACTIVE_POLICIES: "No active stops",
  WEBSOCKET_OBSERVING: "Live updates",
  HTTP_OBSERVING: "Scheduled price checks",
  WEBSOCKET_DISCONNECTED: "Live updates disconnected; scheduled checks continue",
  WEBSOCKET_TRANSPORT_FAILED: "Live connection lost; scheduled checks continue",
  WEBSOCKET_DECODE_FAILED: "Live update rejected; scheduled checks continue",
  HTTP_OBSERVATION_FAILED: "Price check failed; using the last accepted observation",
  OBSERVATION_STALE: "Fresh prices unavailable",
};

/**
 * The contract carries no per-amount reason for a missing USD price (PreviewAmount/EarningsAmount
 * only have `usd: null`), so these fixed strings explain the placeholders THE RULE forbids leaving
 * bare. They state the unavailability itself, never a substitute value.
 */
const USD_VALUE_UNAVAILABLE = "USD value unavailable";
/** What the measured fee return divides by; the line itself has no room to say it. */
const FEE_RETURN_BASIS = "Fees as a percent of the liquidity they were earned on";
const OPENING_TIME_UNAVAILABLE = "opening time unavailable";
const REWARD_AMOUNT_UNAVAILABLE = "reward amount unavailable";

function displayMoney(value: string | null) {
  const formatted = formatMoney(value);
  return formatted === "Unavailable" ? "—" : formatted;
}

function displayPercent(value: string | null) {
  const formatted = formatPercent(value);
  return formatted === "Unavailable" ? "—" : formatted;
}

function valueTone(value: string | null) {
  if (value === null || value === "" || !Number.isFinite(Number(value))) return "unknown";
  if (Number(value) > 0) return "positive";
  if (Number(value) < 0) return "negative";
  return "";
}

function metric(
  label: string,
  value: string | null,
  // "percent" is an operator-input echo (displayPercent); "money-percent" is a money-derived
  // result (lifetime fee APR) that goes through the bounded formatPercent2 instead (R24).
  kind: "money" | "percent" | "money-percent" = "money",
  note?: string,
) {
  return (
    <div className="position-metric">
      <small title={note}>{label}</small>
      <b className={value === null ? "unknown" : label === "APR" ? valueTone(value) : ""}>
        {kind === "money"
          ? displayMoney(value)
          : kind === "money-percent"
            ? formatPercent2(value)
            : displayPercent(value)}
      </b>
    </div>
  );
}

function tokenName(position: Position, mint: string) {
  return (
    [position.tokenA, position.tokenB].find((token) => token.mint === mint)?.symbol ?? shorten(mint)
  );
}

function policyStops(policy: Position["exitPolicy"], visible: boolean) {
  return {
    lowerStop: visible && policy.side !== "above" ? policy.lowerTrigger : undefined,
    upperStop: visible && policy.side !== "below" ? policy.upperTrigger : undefined,
  };
}

function policySummary(
  position: Position,
  policy: Position["exitPolicy"],
  inactiveWhileOpening = false,
) {
  const target = tokenName(position, policy.targetMint);
  if (inactiveWhileOpening) {
    const { lowerStop, upperStop } = policyStops(policy, true);
    const saved =
      lowerStop || upperStop ? `Saved · ${stopPolicyText(policy)}` : "No saved stop triggers";
    return `${saved} · inactive while opening · return to ${target}`;
  }
  if (!policy.enabled) return `Off · return to ${target}`;
  return `${stopPolicyText(policy)} · return to ${target}`;
}

function compactPolicySummary(
  position: Position,
  policy: Position["exitPolicy"],
  opening: boolean,
  hasSavedStops: boolean,
  autopilotOwned: boolean,
) {
  const target = tokenName(position, policy.targetMint);
  if (opening)
    return hasSavedStops
      ? `Saved auto-exit · inactive while opening · return to ${target}`
      : `Auto-exit inactive while opening · return to ${target}`;
  if (!policy.enabled) return `Auto-exit off · return to ${target}`;
  // The autopilot's stop is never editable (design 47, section 7): the footer says who set it.
  const setBy = autopilotOwned ? " · set by the autopilot" : "";
  return `Auto-exit armed · ${stopPolicyText(policy)} · return to ${target}${setBy}`;
}

/** A percentage that already carries its sign (a server `RangeFrame` offset), shown as −x% / +x%. */
function signedPercent(value: string) {
  return signedOffset(value, "upper");
}

function signedOffset(value: string, edge: "lower" | "upper") {
  const negative = value.startsWith("-");
  const magnitude = negative ? value.slice(1) : value;
  if (Number(magnitude) === 0) return displayPercent("0");
  const sign = edge === "lower" ? (negative ? "+" : "−") : negative ? "−" : "+";
  return `${sign}${displayPercent(magnitude)}`;
}

function ResultRow({
  label,
  value,
  reason,
  tone = "",
}: {
  label: string;
  value: string;
  reason?: string;
  tone?: string;
}) {
  return (
    <div className="position-result-row">
      <dt>{label}</dt>
      <dd className={tone}>
        <span>{value}</span>
        {reason && <small>{reason}</small>}
      </dd>
    </div>
  );
}

/**
 * Annualized fee rate with its tier: a money-derived server percent (never a signed result), an
 * amber "indicative" flag, or `—` and why.
 */
function RateNote({
  label,
  percent,
  quality,
  reason,
}: {
  label: string;
  percent: string | null;
  quality: RateQuality | null;
  reason: string | null;
}) {
  if (percent === null)
    return (
      <>
        {label} —{reason === null ? "" : ` · ${reason}`}
      </>
    );
  return (
    <>
      {label} {formatPercent2(percent)}
      {quality === "indicative" && <span className="warn"> · indicative</span>}
    </>
  );
}

function EarningsHeading({ title, note }: { title: string; note?: string }) {
  return (
    <div className="earnings-heading">
      <h3>{title}</h3>
      {note !== undefined && <span>{note}</span>}
    </div>
  );
}

function EarningsRow({
  label,
  swatch,
  value,
  tone,
  note,
  total,
  indent,
  subtotal,
  noteRow,
}: {
  label: string;
  swatch?: "a" | "b";
  value: string;
  tone?: string;
  note?: ReactNode;
  total?: boolean;
  /** The indented "of which" row under Original capital's SOL committed row. */
  indent?: boolean;
  /** The Mark-to-market PnL band inside the breakdown, on the `--surface-2` background. */
  subtotal?: boolean;
  /** An informational "incl." memo row (unswapped rewards, retained/closing rent): no sign tone. */
  noteRow?: boolean;
}) {
  return (
    <div
      className={`earnings-row${total ? " earnings-total" : ""}${indent ? " earnings-row-indent" : ""}${subtotal ? " earnings-subtotal" : ""}${noteRow ? " earnings-note-row" : ""}`}
    >
      <span className="earnings-row-label">
        {swatch && <i className={`earnings-swatch pool-${swatch}`} aria-hidden="true" />}
        {label}
      </span>
      <span className={`num earnings-row-value ${noteRow ? "" : (tone ?? "")}`}>{value}</span>
      <span className="num earnings-row-note">{note}</span>
    </div>
  );
}

/** A group caption inside "Where the result came from" (In the pool / Opening / Closing). */
function EarningsGroup({ title }: { title: string }) {
  return <div className="earnings-group">{title}</div>;
}

/** Joins the parts a caption actually has, so an unavailable one never leaves a bare separator. */
function dotted(...parts: (string | null)[]) {
  return parts.filter((part) => part !== null).join(" · ");
}

/** Unclaimed fees and non-zero rewards as exact native amounts; `—` when a reward scale is unknown. */
function earningsAmounts(position: Position, earnings: PositionEarnings) {
  return [
    ...earnings.fees.map((fee) => ({
      key: `fee-${fee.mint}`,
      amount: `${formatToken(fee.decimal)} ${tokenName(position, fee.mint)}`,
      reason: undefined as string | undefined,
    })),
    ...earnings.rewards.map((reward) => ({
      key: `reward-${reward.mint}`,
      amount: `${reward.decimal === null ? "—" : formatToken(reward.decimal)} ${tokenName(position, reward.mint)}`,
      reason: reward.reason ?? (reward.decimal === null ? REWARD_AMOUNT_UNAVAILABLE : undefined),
    })),
  ];
}

const EXIT_QUOTE_PENDING_LABEL = "Exit quote pending";
const EXIT_QUOTE_PENDING_NOTE = "exit quote pending";

type ExitStripState = "mark-to-market" | "dash-with-mark" | "no-record" | "pending";

/**
 * Which strip/tile treatment each exit-quote reason code gets (R24, B5). `satisfies Record<...>`
 * makes the compiler reject a union member left without a branch when the contract grows.
 */
const EXIT_STRIP_STATE = {
  "no-opening-record": "no-record",
  withdrawn: "mark-to-market",
  "close-in-progress": "mark-to-market",
  "evidence-unavailable": "mark-to-market",
  "trading-not-enabled": "mark-to-market",
  "impact-over-cap": "dash-with-mark",
  "swap-not-acceptable": "dash-with-mark",
  "close-unsupported-reward": "mark-to-market",
  "close-not-preparable": "mark-to-market",
  "reserve-shortfall": "dash-with-mark",
  "quote-failed": "mark-to-market",
  "quote-pending": "pending",
} satisfies Record<ExitQuoteReasonCode, ExitStripState>;

type ExitOutcome =
  | { kind: "available" }
  /** exitQuote.status is "available" but the exit USD total is unmarked (PC14/PC30, DS-B1). */
  | { kind: "sol-only" }
  | { kind: "mark-to-market" }
  /**
   * The quote is being computed in the background and arrives with a later snapshot: a muted
   * placeholder, never a figure, and not the amber failure treatment. Keyed off the reason CODE
   * only, so the server's reason text can change without affecting this state.
   */
  | { kind: "pending" }
  | { kind: "dash-with-mark" }
  | { kind: "no-record" }
  /** markToMarket.usd is also null (PC17): no number survives at all. */
  | { kind: "no-mark" };

/** Classifies the exit quote once so the strip cells and "If closed now" tiles agree (B5/PC17). */
function exitOutcome(earnings: PositionEarnings): ExitOutcome {
  const quote = earnings.exitQuote;
  if (quote.status === "available")
    return earnings.exitPnl.usd === null ? { kind: "sol-only" } : { kind: "available" };
  const reasonCode = quote.reasonCode;
  if (reasonCode === null || reasonCode === "no-opening-record") return { kind: "no-record" };
  const bucket = EXIT_STRIP_STATE[reasonCode];
  // Pending outranks a missing mark: the quote is on its way, which is what the cell must say.
  if (bucket === "pending") return { kind: "pending" };
  if (earnings.markToMarket.usd === null) return { kind: "no-mark" };
  return bucket === "mark-to-market" ? { kind: "mark-to-market" } : { kind: "dash-with-mark" };
}

/** `earnings.capital.mint`'s token symbol; `null` when the server named no funding mint (PC16). */
function fundingSymbol(position: Position, earnings: PositionEarnings): string | null {
  return earnings.capital.mint === null ? null : tokenName(position, earnings.capital.mint);
}

/**
 * Definitions "rewards not included": whenever the exit quote carries an unswapped (third-mint)
 * reward, every NATIVE result line gets this caption — never a USD figure, which already includes
 * the reward's value when it is priced (r3 O4).
 */
function rewardsCaption(earnings: PositionEarnings): string {
  return earnings.exitQuote.unswapped.length > 0 ? " · rewards not included" : "";
}

/** The headline only consumes the expense-inclusive projection, including on older payloads. */
function exitValueParts(position: Position, earnings: PositionEarnings): ReactNode {
  const cash = earnings.fundingCashResult;
  const symbol = cash?.fundingMint ? tokenName(position, cash.fundingMint) : "";
  return (
    <>
      <span className={signTone(cash?.net)}>{formatSignedPercent(cash?.percent)}</span>
      <span className={`metric-amount ${signTone(cash?.net)}`}>
        {formatSignedAmount(cash?.net, symbol)}
      </span>
    </>
  );
}

/**
 * The pending exit value uses the filled value's two slots: a muted, labelled placeholder in the
 * percent slot and the separately labelled mark-to-market in the amount slot. The amount slot keeps
 * the 17px figure's line box (the text inside is sub-line sized), so it occupies the lines a filled
 * amount would, and the one sub-line below takes the USD line's place — nothing moves when the
 * quote fills in.
 */
function exitPendingValue(earnings: PositionEarnings): ReactNode {
  return (
    <>
      <span className="unknown" role="img" aria-label={EXIT_QUOTE_PENDING_LABEL}>
        …
      </span>
      <span className="metric-amount exit-pending-slot">
        <span className="exit-pending-note">
          Mark-to-market {formatSignedAmount(earnings.markToMarket.usd)}
        </span>
      </span>
    </>
  );
}

/**
 * Sub-lines while the quote is pending, one per filled sub-line: "exit quote pending" in the USD
 * line's place; the equivalence and exclusion lines keep the filled state's conditions (both come
 * from the opening record and the snapshot's rewards, which the quote does not change). No reason
 * text and no amber warning: pending is not a failure.
 */
function exitPendingSubLines(position: Position, earnings: PositionEarnings): ReactNode {
  const cash = earnings.fundingCashResult;
  return (
    <>
      {/* The placeholder's label already announces it; this is the visible half. */}
      <span className="metric-reason" aria-hidden="true">
        {EXIT_QUOTE_PENDING_NOTE}
      </span>
      {cash?.classification === "equivalent" && (
        <span>Equivalent in original funding currency</span>
      )}
      {cash?.exclusions.map((r) => (
        <span className="warn" key={r.mint}>
          {tokenName(position, r.mint)}: {r.native} native · {r.reason}; total unavailable
        </span>
      ))}
    </>
  );
}

function exitSubLines(
  position: Position,
  earnings: PositionEarnings,
  outcome: ExitOutcome,
): ReactNode {
  const cash = earnings.fundingCashResult;
  return (
    <>
      <span className={`cash-usd-secondary ${signTone(cash?.usd.net)}`}>
        USD {formatSignedPercent(cash?.usd.percent)} · {formatSignedAmount(cash?.usd.net)}
      </span>
      {cash?.classification === "equivalent" && (
        <span>Equivalent in original funding currency</span>
      )}
      {(cash?.reason ?? cash?.percentReason ?? (!cash ? "cash result unavailable" : null)) && (
        <span className="metric-reason">
          {cash?.reason ?? cash?.percentReason ?? "cash result unavailable"}
        </span>
      )}
      {cash?.usd.reason && <span className="metric-reason">USD · {cash.usd.reason}</span>}
      {cash?.exclusions.map((r) => (
        <span className="warn" key={r.mint}>
          {tokenName(position, r.mint)}: {r.native} native · {r.reason}; total unavailable
        </span>
      ))}
      {earnings.exitQuote.staleReason && (
        <span className="warn">{earnings.exitQuote.staleReason}</span>
      )}
      {earnings.exitQuote.status !== "available" && (
        <span className="warn">{earnings.exitQuote.reason}</span>
      )}
      {(outcome.kind === "mark-to-market" || outcome.kind === "dash-with-mark") && (
        <span className="metric-reason">
          Mark-to-market {formatSignedAmount(earnings.markToMarket.usd)}
        </span>
      )}
    </>
  );
}

/** Which of the vs {F} HODL cell's four number states applies (B1, PC14/PC30/PC17). */
function vsFundingHodlOutcome(earnings: PositionEarnings, outcome: ExitOutcome) {
  const vs = earnings.vsFundingHodl;
  // `basis` decides which comparison this tile states, never the exit cell's outcome beside it: a
  // "cash" row must not borrow the wide caption just because the exit dollars happen to be priced.
  if (outcome.kind === "available" || outcome.kind === "sol-only")
    return vs.basis === "total" && vs.usd !== null
      ? ("available" as const)
      : vs.amount !== null
        ? ("sol-only" as const)
        : ("dash" as const);
  if (outcome.kind === "no-record") return "no-record" as const;
  return "dash" as const;
}

/**
 * The vs {F} HODL cell/tile value: one basket in two units, so the percent, the dollars and the
 * funding-mint amount always share a sign. Each is still coloured by ITS OWN value — a number that
 * rounds to zero must read neutral rather than take the colour of its neighbour.
 */
function vsFundingHodlParts(
  position: Position,
  earnings: PositionEarnings,
  state: ReturnType<typeof vsFundingHodlOutcome>,
) {
  const vs = earnings.vsFundingHodl;
  const symbol = fundingSymbol(position, earnings) ?? "";
  if (state === "available")
    return (
      <>
        <span className={signTone(vs.percent)}>{formatSignedPercent(vs.percent)}</span>
        <span className={`metric-amount ${signTone(vs.usd)}`}>{formatSignedAmount(vs.usd)}</span>
        <span className={`metric-amount ${signTone(vs.amount)}`}>
          {formatSignedSolShort(vs.amount)} {symbol}
        </span>
      </>
    );
  if (state === "sol-only")
    return (
      <>
        <span className={signTone(vs.percent)}>{formatSignedPercent(vs.percent)}</span>
        <span className={`metric-amount ${signTone(vs.amount)}`}>
          {formatSignedSolShort(vs.amount)} {symbol}
          {rewardsCaption(earnings)}
        </span>
      </>
    );
  return <span className="unknown">—</span>;
}

/** The vs {F} HODL cell/tile caption for each of its four number states. */
function vsFundingHodlCaption(
  state: ReturnType<typeof vsFundingHodlOutcome>,
  fundingSymbolValue: string | null,
  capitalFunding: string | null,
): string {
  if (state === "available")
    return `vs keeping ${formatSolShort(capitalFunding)} ${fundingSymbolValue ?? ""}`;
  // The narrower basket, named: without a mark only the cash back can be compared exactly.
  if (state === "sol-only") return `cash back only · in ${fundingSymbolValue ?? ""}`;
  if (state === "no-record") return "no opening record";
  return "needs exit quote";
}

export function PositionCard({
  position,
  onClose,
  onPolicy,
  onTakeControl,
  runtimeMonitor,
  expanded: controlledExpanded,
  onToggle,
  onOperation,
  onRestart,
  onResume,
  actionPending,
  lastKnown,
  openingFunding,
  ringed,
}: {
  position: Position;
  onClose: () => void;
  onPolicy?: () => void;
  /** "Take manual control" for a position the autopilot owns (`position.owner === "autopilot"`). */
  onTakeControl?: () => void;
  runtimeMonitor?: RuntimeStatus["rpc"] | null;
  expanded?: boolean;
  actionPending?: boolean;
  lastKnown?: boolean;
  ringed?: boolean;
  openingFunding?: PreviewAmount | null;
  onToggle?: () => void;
  onOperation?: () => void;
  onRestart?: () => void;
  onResume?: (operation: Operation) => void;
}) {
  const autopilotOwned = position.owner === "autopilot";
  // Close, stop settings and take-over are commands: a viewer's card has none of them.
  const viewer = useViewer();
  const policyAction = viewer ? undefined : onPolicy;
  const takeControlAction = viewer ? undefined : onTakeControl;
  const [localExpanded, setLocalExpanded] = useState(false);
  /** The range block opens in the quote the position was opened in; ⇄ shows the reciprocal one. */
  const [rangeFlipped, setRangeFlipped] = useState(false);
  const expanded = controlledExpanded ?? localExpanded;
  const toggle =
    onToggle ??
    (() => {
      setLocalExpanded((v) => !v);
    });
  const policy = position.exitPolicy;
  // One frame for the whole card: the header, both mini scales, the expanded panels and every
  // policy summary below read display sides/prices out of this single conversion.
  const frame = priceFrame(position.tokenA, position.tokenB);
  const displayPolicy = frame.policy(policy);
  const displayBounds = frame.bounds(position.lowerPrice, position.upperPrice);
  const displayCurrentPrice = frame.priceOrNull(position.currentPrice);
  const opening = position.monitorState === "opening";
  const funding =
    openingFunding === undefined ? position.operation?.opening?.funding : openingFunding;
  const operation = position.operation;
  const operationView = operation ? operationPresentation(operation) : null;
  // Read from the journal's own state, never from the presentation mode: whether the engine still
  // owns this exit is durable state, not something the card may re-derive from a failure code. The
  // state line itself is rendered once, by the operation panel below the header.
  const armedExit = operation?.automaticExit?.state === "armed";
  const visibleStops = opening || policy.enabled;
  // `policyStops` runs on the DISPLAY policy so its `side !== "above"` gate and the triggers it
  // returns are in one frame; the mini scales then take `lowerStop`/`upperStop` directly.
  const { lowerStop, upperStop } = policyStops(displayPolicy, visibleStops);
  const hasSavedStops = opening && (Boolean(lowerStop) || Boolean(upperStop));
  const policyText = opening
    ? hasSavedStops
      ? `Saved automatic exit settings inactive while opening · return to ${tokenName(position, policy.targetMint)}`
      : `Automatic exit inactive while opening · no saved stops · return to ${tokenName(position, policy.targetMint)}`
    : policy.enabled
      ? `Current automatic exit ${stopPolicyText(displayPolicy)} · to ${tokenName(position, policy.targetMint)}`
      : `Current automatic exit off · return to ${tokenName(position, policy.targetMint)}`;
  const rangeState = opening
    ? (operationView?.chip ?? "Opening")
    : position.inRange === null
      ? "Range unknown"
      : position.inRange
        ? "Active · in range"
        : "Out of range";
  const terminalOperation = operation && ["completed", "cancelled"].includes(operation.state);
  const closeRunning = operation?.kind === "close" && !terminalOperation;
  // A close settled needs-attention awaits the operator; releasing it settles the autopilot cycle.
  const takeoverOpen = !closeRunning || operation.state === "needs-attention";
  const statusTone =
    (opening || closeRunning) && operationView
      ? operationView.tone
      : position.inRange === null
        ? "muted"
        : position.inRange
          ? "green"
          : "amber";
  const monitor = opening
    ? { tone: "warn", text: "opening" }
    : policy.enabled
      ? (monitorPresentation[runtimeMonitor?.state ?? ""] ?? { tone: "", text: "unknown" })
      : { tone: "", text: "off" };
  const detail = !opening && policy.enabled ? runtimeMonitor?.monitor : undefined;
  const readOnlyLabel = "Close unavailable: read-only position";
  const compatibleOpening =
    position.openingSetup?.baseMint === position.tokenA.mint &&
    position.openingSetup.quoteMint === position.tokenB.mint
      ? position.openingSetup
      : null;
  // `compatibleOpening` only matches when the setup frame equals canonical (base = tokenA, quote =
  // tokenB), so its reference price is a canonical sample and may be re-oriented like one.
  const fallbackEntry = compatibleOpening
    ? (frame.priceOrNull(compatibleOpening.referencePrice) ?? undefined)
    : undefined;
  // The optional field gates the whole earnings presentation: opening rows and adapters that do
  // not value positions keep the previous strip and body instead of dereferencing missing data.
  const earnings = opening ? undefined : position.earnings;
  // A known dollar amount is still shown even without a percent; it never stands in for the
  // missing percent or for its reason (used by the expanded "vs Pair HODL" tile only — the
  // compact strip's third cell is Fee APR, per R-F).
  const hodlReason =
    earnings === undefined
      ? undefined
      : earnings.hodl.vsHodlUsd !== null
        ? (earnings.hodl.vsHodlReason ?? earnings.hodl.reason)
        : (earnings.hodl.reason ?? earnings.hodl.vsHodlReason);
  const earnedAmounts = earnings ? earningsAmounts(position, earnings) : [];
  /** The exact window the measured fee return covers; null when the opening time is unknown. */
  const feeWindow = earnings ? formatSpan(earnings.timeInLpMs) : null;
  const breakdown = position.pnlBreakdown;
  // A routed entry swap's fee row is its realized cost, not the pool fee: name the aggregator.
  const entryRoute = breakdown?.incurredCosts.actualSwaps?.find((swap) => swap.provider)?.provider;
  const totalAssets = breakdown?.status === "available" ? breakdown.totalAssetsUsd : null;
  const baselineCapital = breakdown?.status === "available" ? breakdown.baselineCapitalUsd : null;
  const comparisonAvailable = totalAssets !== null && baselineCapital !== null;
  const target = tokenName(position, policy.targetMint);
  const auditReason = breakdown?.reason ?? "Complete current assets are not provided";
  const comparisonReason =
    totalAssets === null
      ? auditReason
      : baselineCapital === null
        ? "Admitted capital baseline is not provided"
        : undefined;

  // Both quotes are attached to the authoritative position state, already measured against the
  // entry price. The card only picks one, so prices and percentages cannot mix orientations. The
  // default is the rule frame (R5); ⇄ still flips, but now means "flipped against the rule".
  const orientations = position.rangeMarks ?? null;
  // The mini scales stay in the rule frame whatever the expanded ⇄ says, so their entry does too.
  const miniEntry = frame.rangeFrame(orientations, false)?.entryPrice ?? fallbackEntry;
  const rangeFrame = frame.rangeFrame(orientations, rangeFlipped);
  const otherRangeFrame = frame.rangeFrame(orientations, !rangeFlipped);
  // `frameScale`'s `stops` argument is documented as CANONICAL sides and handles a flipped
  // `RangeFrame` itself, so it takes the canonical visibility here, never the display policy.
  const rangeGeometry = rangeFrame
    ? frameScale(rangeFrame, {
        lower: visibleStops && policy.side !== "above",
        upper: visibleStops && policy.side !== "below",
      })
    : null;
  const rangeUnit = rangeFrame
    ? frame.unitOf(rangeFrame.baseMint, rangeFrame.quoteMint)
    : frame.unit;
  const flippedUnit = otherRangeFrame
    ? frame.unitOf(otherRangeFrame.baseMint, otherRangeFrame.quoteMint)
    : null;

  const rangeSection = (
    <section className="position-range-section" aria-label="Position range">
      <div className="position-section-heading">
        <h3>
          Range
          {rangeFrame && (
            <span className="range-requested">
              {" · set at open "}
              <span className="num">
                {formatSignedPercent(rangeFrame.requestedLowerPercent)} /{" "}
                {formatSignedPercent(rangeFrame.requestedUpperPercent)}
              </span>
            </span>
          )}
        </h3>
        <div className="position-range-heading-actions">
          <span>{rangeUnit}</span>
          {flippedUnit && (
            <button
              type="button"
              className="button ghost small range-flip"
              aria-label={`Show the range as ${flippedUnit}`}
              onClick={() => {
                setRangeFlipped((v) => !v);
              }}
            >
              ⇄
            </button>
          )}
          {opening &&
            !position.readOnly &&
            policyAction &&
            !closeRunning &&
            !actionPending &&
            !autopilotOwned && (
              <button className="button ghost small" onClick={policyAction}>
                {hasSavedStops ? "Stop settings" : "Configure stop"}
              </button>
            )}
        </div>
      </div>
      <PriceScale
        compact
        min={rangeGeometry?.min ?? displayBounds.lower}
        max={rangeGeometry?.max ?? displayBounds.upper}
        current={rangeGeometry ? rangeGeometry.current : displayCurrentPrice}
        lowerStop={rangeGeometry ? rangeGeometry.lowerStop : lowerStop}
        upperStop={rangeGeometry ? rangeGeometry.upperStop : upperStop}
        inactiveStops={hasSavedStops}
        entry={rangeGeometry?.entry ?? fallbackEntry}
        percents={rangeGeometry?.percents ?? null}
        unit={rangeUnit}
      />
      {hasSavedStops && (
        <small className="opening-stop-note">Saved stops · inactive while opening</small>
      )}
    </section>
  );

  return (
    <article
      id={`position-${position.id}`}
      className={`position-card compact-position-card ${closeRunning ? "position-exiting" : ""} ${earnings ? "earnings-card" : ""} ${expanded ? "expanded" : ""} ${ringed ? "ringed" : ""}`}
    >
      <header className="position-card-header">
        <TokenPair frame={frame} />
        <div className="position-identity-copy">
          <div className="tag-line">
            <h2 id={`position-heading-${position.id}`} tabIndex={-1}>
              <TokenPairName frame={frame} />
            </h2>
            <span className="tag purple num">{displayPercent(position.feePercent)}</span>
            <span className={`tag lifecycle ${statusTone}`}>
              <i
                className={`status-dot ${statusTone === "green" ? "good" : statusTone === "amber" ? "warn" : statusTone === "red" ? "bad" : ""}`}
              />
              {closeRunning ? operationView?.chip : rangeState}
            </span>
            {autopilotOwned && <span className="tag autopilot-chip">Autopilot</span>}
            <DexBadge dex={position.dex} />
          </div>
          <div className="position-meta">
            <span className="num" title={position.id}>
              {shorten(position.id, 7)}
            </span>
            <span aria-hidden="true">·</span>
            {opening ? (
              <span>Started {formatAgo(position.createdAt)}</span>
            ) : (
              <span
                title={
                  position.openedAt
                    ? (earnings?.timeReason ?? undefined)
                    : (earnings?.timeReason ?? OPENING_TIME_UNAVAILABLE)
                }
              >
                Opened {position.openedAt ? formatAgo(position.openedAt) : "—"}
                {autopilotOwned && " by the autopilot"}
              </span>
            )}
            <span aria-hidden="true">·</span>
            <a
              className="pool-link position-pool-link"
              href={dexPoolUrl(position.dex, position.poolAddress)}
              target="_blank"
              rel="noreferrer"
            >
              {dexName(position.dex)} pool
              <svg viewBox="0 0 12 12" aria-hidden="true">
                <path d="M4.5 2.5h5v5M9.5 2.5 3 9" />
              </svg>
            </a>
            {[position.tokenA, position.tokenB].some(
              (token) => token.usesScaledUiAmount === true,
            ) && (
              <span className="scaled-pool-units-compact">
                Pool units (unscaled) · wallet display may differ
              </span>
            )}
          </div>
        </div>
        <div className="position-actions">
          {!opening &&
            !position.readOnly &&
            policyAction &&
            !closeRunning &&
            !actionPending &&
            !autopilotOwned && (
              <button className="button ghost small" onClick={policyAction}>
                {policy.enabled ? "Change stop" : "Configure stop"}
              </button>
            )}
          {!opening && autopilotOwned && takeControlAction && takeoverOpen && !actionPending && (
            <button className="button ghost small" onClick={takeControlAction}>
              Take manual control
            </button>
          )}
          {!opening && !viewer && (
            <button
              className="button danger close-position"
              onClick={onClose}
              disabled={(actionPending ?? false) || position.readOnly === true || closeRunning}
              title={
                position.readOnly
                  ? readOnlyLabel
                  : closeRunning
                    ? armedExit
                      ? "The automatic stop owns this close and will run it itself"
                      : "A close operation is already in progress"
                    : undefined
              }
              aria-label={position.readOnly ? readOnlyLabel : undefined}
            >
              {position.readOnly
                ? "Read only"
                : closeRunning
                  ? armedExit
                    ? "Stop armed"
                    : "Closing…"
                  : position.canCancelOpening
                    ? "Cancel opening"
                    : `Close → ${target}`}
            </button>
          )}
          <button
            className="icon-button expand-position"
            aria-label={expanded ? "Collapse position" : "Expand position"}
            aria-expanded={expanded}
            aria-controls={`expanded-${position.id}`}
            onClick={toggle}
          >
            <svg viewBox="0 0 14 14" aria-hidden="true">
              <path d="m3.5 5.5 3.5 3.5 3.5-3.5" />
            </svg>
          </button>
        </div>
      </header>
      {lastKnown && (
        <p className="inline-alert warning">
          Last known position values. Awaiting the final operation result.
        </p>
      )}
      {position.poolPaused && (
        <p className="inline-alert warning">
          Pool paused by its admin: {dexName(position.dex)} has withdrawals, fee collection or swaps
          turned off for this pool. A close can be refused until it reopens; the stops keep watching
          and retry on their own.
        </p>
      )}
      {position.operation && (opening || closeRunning) && (
        <OperationStatus
          key={`${position.operation.id}:${position.operation.state}:${position.operation.intentRevision?.activeIntentHash ?? "legacy"}`}
          operation={position.operation}
          compact
          actionPending={actionPending}
          onCancel={onClose}
          onRestart={onRestart}
          onResume={onResume}
          onChanged={onOperation}
        />
      )}
      {earnings ? (
        (() => {
          const outcome = exitOutcome(earnings);
          const solHodlState = vsFundingHodlOutcome(earnings, outcome);
          const fundingSymbolValue = fundingSymbol(position, earnings);
          return (
            <div className="compact-metrics earnings-metrics" aria-label="Position metrics">
              <section className="exit-cell">
                <small>Exit now PnL</small>
                <b className="num metric-pair">
                  {outcome.kind === "pending"
                    ? exitPendingValue(earnings)
                    : exitValueParts(position, earnings)}
                </b>
                {outcome.kind === "pending"
                  ? exitPendingSubLines(position, earnings)
                  : exitSubLines(position, earnings, outcome)}
              </section>
              <section className="funding-hodl-cell">
                <small>
                  {fundingSymbolValue === null ? "vs HODL" : `vs ${fundingSymbolValue} HODL`}
                </small>
                <b className="num metric-pair">
                  {vsFundingHodlParts(position, earnings, solHodlState)}
                </b>
                <span className="metric-reason">
                  {vsFundingHodlCaption(solHodlState, fundingSymbolValue, earnings.capital.funding)}
                </span>
              </section>
              <section className="fee-apr-cell">
                <small>Fee APR</small>
                <b className={`num ${earnings.feeAprPercent === null ? "unknown" : ""}`}>
                  {earnings.feeAprPercent === null ? "—" : formatPercent2(earnings.feeAprPercent)}
                </b>
                {earnings.feeAprPercent === null ? (
                  <span className="metric-reason">{earnings.feeAprReason}</span>
                ) : earnings.feeAprQuality === "indicative" ? (
                  <span className="warn">indicative</span>
                ) : (
                  <span className="metric-reason">annualized</span>
                )}
              </section>
              <section className="fees-cell">
                <small>Fees earned</small>
                <b
                  className={`num ${earnings.earnedUsd === null ? "unknown" : signTone(earnings.earnedUsd) === "positive" ? "positive" : ""}`}
                >
                  {earnings.earnedUsd === null ? "—" : displayMoney(earnings.earnedUsd)}
                </b>
                {earnings.earnedUsd === null && (
                  <span className="metric-reason">{earnings.earnedReason}</span>
                )}
                {earnedAmounts.length > 0 && (
                  /* Phone widths hide the token amounts in favour of the fee percent and the
                     per-day line below (user 26.10); the expanded card keeps the amounts. */
                  <span className="fee-amounts">
                    {earnedAmounts.map((entry, index) => (
                      <Fragment key={entry.key}>
                        {index > 0 && <span className="fee-sep"> · </span>}
                        <span className="fee-amount" title={entry.reason}>
                          {entry.amount}
                        </span>
                      </Fragment>
                    ))}
                  </span>
                )}
                {/* The measured counterpart of the New position panel's estimated yield: fees over
                    the value of the liquidity, for the window stated beside it. Fees only, so it
                    matches the forecast and the APR rather than the rewards-inclusive total above.
                    The basis lives in the title, not the line: under the "Fees earned" heading a
                    bare percent already reads as fees, and the cell is one narrow column. */}
                {earnings.feeReturnPercent !== null && (
                  <span className="fee-return" title={FEE_RETURN_BASIS}>
                    {formatPercent3(earnings.feeReturnPercent)}
                    {feeWindow === null ? "" : ` in ${feeWindow}`}
                  </span>
                )}
                {earnings.feesPerDayUsd !== null && (
                  <span className="fee-per-day">{formatUsd(earnings.feesPerDayUsd)}/day</span>
                )}
                {earnings.feesUnderstated !== null && (
                  <span title={earnings.feesUnderstated}>may be understated</span>
                )}
              </section>
              <section className="lp-cell">
                <small>LP value</small>
                <b className={`num ${position.balanceUsd === null ? "unknown" : ""}`}>
                  {displayMoney(position.balanceUsd)}
                </b>
                {position.balanceUsd === null && (
                  <span className="metric-reason">{USD_VALUE_UNAVAILABLE}</span>
                )}
                {earnings.heldAsideUsd === null ? (
                  <span className="lp-note metric-reason">{earnings.heldAsideReason}</span>
                ) : (
                  <span className="lp-note">+{formatUsd(earnings.heldAsideUsd)} held aside</span>
                )}
              </section>
              <section className="range-cell">
                <small>Range</small>
                <PriceScale
                  mini
                  min={displayBounds.lower}
                  max={displayBounds.upper}
                  current={displayCurrentPrice}
                  lowerStop={lowerStop}
                  upperStop={upperStop}
                  entry={miniEntry}
                  unit={frame.unit}
                  // Below 1 a 2-decimal truncation prints "0.00" for every mark (a rule-frame
                  // unit like SOL per STONK can sit at ~0.003) — return null there so PriceScale
                  // falls back to its sibling-aware compactDisplay, which keeps enough precision.
                  labelFormatter={(v) => (Math.abs(Number(v)) >= 1 ? formatRangeLabel(v) : null)}
                />
              </section>
              <section className="auto-exit-cell">
                <small>Auto-exit</small>
                <b>{policy.enabled ? "Enabled" : "Off"}</b>
                <span>{`${stopPolicyText(displayPolicy)} · → ${target}`}</span>
              </section>
            </div>
          );
        })()
      ) : (
        <div className="compact-metrics supported-metrics" aria-label="Position metrics">
          <section>
            <small>{opening ? "Fees" : "Unclaimed fees USD"}</small>
            <b className="num">{opening ? "—" : displayMoney(position.pendingFeesUsd)}</b>
            <span>
              {opening
                ? "after opening"
                : position.pendingFeesUsd === null
                  ? "valuation unavailable"
                  : position.aprPercent === null
                    ? "APR unavailable"
                    : `APR ${formatPercent2(position.aprPercent)}`}
            </span>
          </section>
          <section>
            <small>{opening ? "Amount being opened" : "LP value"}</small>
            <b className="num">
              {opening
                ? funding
                  ? displayMoney(funding.usd)
                  : "—"
                : displayMoney(position.balanceUsd)}
            </b>
            <span>
              {opening
                ? funding
                  ? `${formatToken(funding.decimal)} ${tokenName(position, funding.mint)}`
                  : "funding unavailable"
                : baselineCapital === null
                  ? "baseline unavailable"
                  : `from ${displayMoney(baselineCapital)}`}
            </span>
          </section>
          <section>
            <small>{operation?.kind === "close" ? "Time since opening" : "Time in LP"}</small>
            <b className="num">
              {opening ? "—" : formatAgo(position.createdAt).replace(/ ago$/, "")}
            </b>
            <span>{opening ? "not started" : "since opening"}</span>
          </section>
          <section>
            <small>{opening ? "Planned range" : "Range"}</small>
            <PriceScale
              mini
              preliminary={opening}
              min={displayBounds.lower}
              max={displayBounds.upper}
              current={displayCurrentPrice}
              lowerStop={lowerStop}
              upperStop={upperStop}
              entry={miniEntry}
              unit={frame.unit}
            />
          </section>
          <section>
            <small>Auto-exit</small>
            <b>{opening ? "Saved" : policy.enabled ? "Enabled" : "Off"}</b>
            <span>
              {opening ? "starts after opening" : `${stopPolicyText(displayPolicy)} · → ${target}`}
            </span>
          </section>
        </div>
      )}
      {expanded && (
        <div id={`expanded-${position.id}`} className="expanded-position">
          {earnings ? (
            (() => {
              const outcome = exitOutcome(earnings);
              const solHodlState = vsFundingHodlOutcome(earnings, outcome);
              const fundingSymbolValue = fundingSymbol(position, earnings);
              return (
                <div className="position-earnings-body">
                  <div className="position-earnings-column">
                    {rangeSection}
                    <section aria-label="In the pool">
                      <EarningsHeading
                        title="In the pool"
                        note={`updated ${formatAgo(position.observedAt)}`}
                      />
                      {earnings.pool === null ? (
                        <p className="earnings-unavailable">— · position observation unavailable</p>
                      ) : (
                        <>
                          {earnings.pool.shareAPercent !== null &&
                            earnings.pool.shareBPercent !== null && (
                              <div className="pool-split" aria-hidden="true">
                                <span
                                  className="pool-a"
                                  style={{ width: `${earnings.pool.shareAPercent}%` }}
                                />
                                <span
                                  className="pool-b"
                                  style={{ width: `${earnings.pool.shareBPercent}%` }}
                                />
                              </div>
                            )}
                          <EarningsRow
                            label={tokenName(position, earnings.pool.tokenA.mint)}
                            swatch="a"
                            value={`${formatToken(earnings.pool.tokenA.decimal)} ${tokenName(position, earnings.pool.tokenA.mint)}`}
                            note={dotted(
                              earnings.pool.tokenA.usd === null
                                ? null
                                : formatUsd(earnings.pool.tokenA.usd),
                              earnings.pool.shareAPercent === null
                                ? null
                                : displayPercent(earnings.pool.shareAPercent),
                            )}
                          />
                          <EarningsRow
                            label={tokenName(position, earnings.pool.tokenB.mint)}
                            swatch="b"
                            value={`${formatToken(earnings.pool.tokenB.decimal)} ${tokenName(position, earnings.pool.tokenB.mint)}`}
                            note={dotted(
                              earnings.pool.tokenB.usd === null
                                ? null
                                : formatUsd(earnings.pool.tokenB.usd),
                              earnings.pool.shareBPercent === null
                                ? null
                                : displayPercent(earnings.pool.shareBPercent),
                            )}
                          />
                          <EarningsRow
                            label="LP value"
                            value={displayMoney(position.balanceUsd)}
                            note={position.balanceUsd === null ? USD_VALUE_UNAVAILABLE : undefined}
                            total
                          />
                        </>
                      )}
                    </section>
                    <section aria-label="Held aside">
                      <EarningsHeading title="Held aside" note="returned on close · not at risk" />
                      {breakdown?.recoverableRent && (
                        <EarningsRow
                          label="Rent deposits"
                          value={displayMoney(breakdown.recoverableRent.usd)}
                          // Rent is always the canonical SOL mint (valuation.ts:482), which the pair's
                          // own token list cannot label on a non-SOL pool.
                          note={dotted(
                            `${formatToken(breakdown.recoverableRent.decimal)} SOL`,
                            breakdown.recoverableRent.usd === null ? USD_VALUE_UNAVAILABLE : null,
                          )}
                        />
                      )}
                      {(breakdown?.residualAndUnusedExpense ?? []).map((amount) => (
                        <EarningsRow
                          key={amount.mint}
                          label={amount.mint === SOL_MINT ? "Unused budget" : "Swap change"}
                          value={displayMoney(amount.usd)}
                          note={dotted(
                            `${formatToken(amount.decimal)} ${tokenName(position, amount.mint)} · in wallet`,
                            amount.usd === null ? USD_VALUE_UNAVAILABLE : null,
                          )}
                        />
                      ))}
                      <EarningsRow
                        label="Total held aside"
                        value={displayMoney(earnings.heldAsideUsd)}
                        note={earnings.heldAsideUsd === null ? earnings.heldAsideReason : undefined}
                        total
                      />
                    </section>
                    <section aria-label="Original capital">
                      <EarningsHeading title="Original capital" note="before the entry swap" />
                      {earnings.capital.usd === null || earnings.capital.funding === null ? (
                        <p className="earnings-unavailable">— · {earnings.capital.reason}</p>
                      ) : (
                        <>
                          <EarningsRow
                            label={`${fundingSymbolValue ?? ""} committed`}
                            value={`${formatSolShort(earnings.capital.funding)} ${fundingSymbolValue ?? ""}`}
                            note={`${displayMoney(earnings.capital.usd)} at ${displayMoney(earnings.capital.markAtOpening)}`}
                          />
                          <EarningsRow
                            label={
                              earnings.capital.mint === SOL_MINT
                                ? "of which expense budget"
                                : "plus expense budget"
                            }
                            value={`${formatSolShort(earnings.capital.budgetSol)} SOL`}
                            note={`${displayMoney(earnings.capital.budgetUsd)} · fees and rent`}
                            indent={earnings.capital.mint === SOL_MINT}
                          />
                        </>
                      )}
                    </section>
                  </div>
                  <div className="position-earnings-column">
                    <section aria-label="If closed now">
                      <div className="earnings-heading">
                        <h3>If closed now</h3>
                        {earnings.exitQuote.status === "available" ? (
                          <span>
                            {`live quote ${formatAgo(earnings.exitQuote.observedAt)} · not a guarantee`}
                            {earnings.exitQuote.staleReason !== null &&
                              ` · ${earnings.exitQuote.staleReason}`}
                          </span>
                        ) : outcome.kind === "pending" ? (
                          <span>{EXIT_QUOTE_PENDING_NOTE}</span>
                        ) : (
                          <span className="warn">{earnings.exitQuote.reason}</span>
                        )}
                      </div>
                      <div className="earnings-tiles">
                        <div className="earnings-tile full-width">
                          <span className="earnings-tile-label">Exit now PnL</span>
                          <span className="num earnings-tile-value">
                            {outcome.kind === "pending"
                              ? exitPendingValue(earnings)
                              : exitValueParts(position, earnings)}
                          </span>
                          <span className="num earnings-tile-note">
                            {outcome.kind === "available" || outcome.kind === "sol-only" ? (
                              <>
                                {exitSubLines(position, earnings, outcome)}
                                <br />
                                Net opening cash spent{" "}
                                {formatToken(earnings.fundingCashResult?.spent ?? null)}{" "}
                                {fundingSymbolValue ?? ""}; includes paid opening expenses, excludes
                                unused reserves.
                                <br />
                                Estimated received assets (including unused reserves):
                                {earnings.fundingCashResult?.receivedAssets.map((entry) => (
                                  <Fragment key={entry.mint}>
                                    <br />
                                    {formatToken(entry.decimal)} {tokenName(position, entry.mint)}
                                  </Fragment>
                                ))}
                                {earnings.exitValue.solBack !== null && (
                                  <>
                                    <br />
                                    {`${formatSolShort(earnings.capital.budgetSol)} SOL budget → ${formatSolShort(earnings.exitValue.solBack)} SOL back`}
                                  </>
                                )}
                                {earnings.exitQuote.unswapped.map((entry) => (
                                  <Fragment key={entry.mint}>
                                    <br />
                                    {`${entry.decimal === null ? "—" : formatToken(entry.decimal)} ${tokenName(position, entry.mint)} · not swapped`}
                                    {entry.usd !== null && ` · ${formatUsd(entry.usd)}`}
                                  </Fragment>
                                ))}
                                {earnings.exitQuote.retainedRent &&
                                  signTone(earnings.exitQuote.retainedRent.sol) === "positive" && (
                                    <>
                                      <br />
                                      {`${formatToken(earnings.exitQuote.retainedRent.sol)} SOL rent kept in token accounts`}
                                      {earnings.exitQuote.retainedRent.usd !== null &&
                                        ` · ${formatUsd(earnings.exitQuote.retainedRent.usd)}`}
                                    </>
                                  )}
                                {earnings.exitQuote.closingRent &&
                                  signTone(earnings.exitQuote.closingRent.sol) === "positive" && (
                                    <>
                                      <br />
                                      {`${formatToken(earnings.exitQuote.closingRent.sol)} SOL rent for new token accounts`}
                                      {earnings.exitQuote.closingRent.usd !== null &&
                                        ` · ${formatUsd(earnings.exitQuote.closingRent.usd)}`}
                                    </>
                                  )}
                              </>
                            ) : outcome.kind === "pending" ? (
                              exitPendingSubLines(position, earnings)
                            ) : (
                              exitSubLines(position, earnings, outcome)
                            )}
                          </span>
                        </div>
                        <div className="earnings-tile">
                          <span className="earnings-tile-label">
                            {fundingSymbolValue === null
                              ? "vs HODL"
                              : `vs ${fundingSymbolValue} HODL`}
                          </span>
                          <span className="num earnings-tile-value">
                            {vsFundingHodlParts(position, earnings, solHodlState)}
                          </span>
                          <span className="num earnings-tile-note">
                            {/* Only the wide basis gets the "keeping N F = $X now" note: on the
                                cash basis there is no capitalNowUsd to state and the figures are
                                the cash back, so the note would contradict the caption beside it. */}
                            {solHodlState === "available" ? (
                              earnings.capital.mint === SOL_MINT ? (
                                `keeping ${formatSolShort(earnings.capital.funding)} SOL = ${displayMoney(earnings.vsFundingHodl.capitalNowUsd)} now · ${formatSignedSolShort(earnings.vsFundingHodl.amount)} SOL${rewardsCaption(earnings)}`
                              ) : (
                                `keeping ${formatSolShort(earnings.capital.funding)} ${fundingSymbolValue ?? ""} + ${formatSolShort(earnings.capital.budgetSol)} SOL = ${displayMoney(earnings.vsFundingHodl.capitalNowUsd)} now · ${formatSignedSolShort(earnings.vsFundingHodl.amount)} ${fundingSymbolValue ?? ""}${rewardsCaption(earnings)}`
                              )
                            ) : (
                              <span className="metric-reason">
                                {vsFundingHodlCaption(
                                  solHodlState,
                                  fundingSymbolValue,
                                  earnings.capital.funding,
                                )}
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="earnings-tile">
                          <span className="earnings-tile-label">vs Pair HODL</span>
                          <span className="num earnings-tile-value">
                            <span className={signTone(earnings.hodl.vsHodlPercent)}>
                              {earnings.hodl.vsHodlPercent === null
                                ? "—"
                                : formatSignedPercent(earnings.hodl.vsHodlPercent)}
                            </span>
                            {earnings.hodl.vsHodlUsd !== null && (
                              <span
                                className={`metric-amount ${signTone(earnings.hodl.vsHodlUsd)}`}
                              >
                                {formatSignedAmount(earnings.hodl.vsHodlUsd)}
                              </span>
                            )}
                          </span>
                          <span className="num earnings-tile-note" title={earnings.hodl.detail}>
                            {earnings.hodl.deposited.length === 2
                              ? `holding ${formatToken(earnings.hodl.deposited[0].decimal)} ${tokenName(position, earnings.hodl.deposited[0].mint)} + ${formatToken(earnings.hodl.deposited[1].decimal)} ${tokenName(position, earnings.hodl.deposited[1].mint)}`
                              : hodlReason}
                          </span>
                        </div>
                      </div>
                    </section>
                    <section aria-label="Fees earned">
                      <EarningsHeading title="Fees earned" note="unclaimed · collected on close" />
                      {earnings.fees.map((fee) => (
                        <EarningsRow
                          key={`fee-${fee.mint}`}
                          label={tokenName(position, fee.mint)}
                          swatch={fee.mint === position.tokenB.mint ? "b" : "a"}
                          value={`${formatToken(fee.decimal)} ${tokenName(position, fee.mint)}`}
                          tone={signTone(fee.native) === "positive" ? "positive" : undefined}
                          note={fee.usd === null ? undefined : formatUsd(fee.usd)}
                        />
                      ))}
                      {earnings.rewards.map((reward) => (
                        <EarningsRow
                          key={`reward-${reward.mint}`}
                          label={`${tokenName(position, reward.mint)} reward`}
                          value={
                            reward.decimal === null
                              ? "—"
                              : `${formatToken(reward.decimal)} ${tokenName(position, reward.mint)}`
                          }
                          tone={
                            reward.decimal !== null && signTone(reward.native) === "positive"
                              ? "positive"
                              : undefined
                          }
                          note={
                            reward.reason ??
                            (reward.decimal === null
                              ? REWARD_AMOUNT_UNAVAILABLE
                              : reward.usd === null
                                ? undefined
                                : formatUsd(reward.usd))
                          }
                        />
                      ))}
                      <EarningsRow
                        label="Total"
                        value={formatSignedAmount(earnings.earnedUsd)}
                        tone={
                          earnings.earnedUsd === null
                            ? "unknown"
                            : signTone(earnings.earnedUsd) === "positive"
                              ? "positive"
                              : undefined
                        }
                        note={
                          // The unavailable total never suppresses the fee rate and daily fees: the
                          // contract excludes rewards from both, so they stay valid on their own.
                          <>
                            {earnings.earnedUsd === null &&
                              earnings.earnedReason !== null &&
                              `${earnings.earnedReason} · `}
                            {earnings.feesPerDayUsd !== null &&
                              `${formatUsd(earnings.feesPerDayUsd)}/day · `}
                            <RateNote
                              label="fee APR"
                              percent={earnings.feeAprPercent}
                              quality={earnings.feeAprQuality}
                              reason={earnings.feeAprReason}
                            />
                          </>
                        }
                        total
                      />
                      {earnings.feesUnderstated !== null && (
                        <p className="earnings-caveat">{earnings.feesUnderstated}</p>
                      )}
                    </section>
                    <section aria-label="Where the result came from">
                      <EarningsHeading
                        title="Where the result came from"
                        note={
                          earnings.capital.usd === null
                            ? undefined
                            : `from ${displayMoney(earnings.capital.usd)} original capital`
                        }
                      />
                      {earnings.breakdown.status === "available" &&
                      earnings.breakdown.closing.status === "available" ? (
                        <>
                          <EarningsGroup title="In the pool" />
                          <EarningsRow
                            label="Price move of Pair HODL"
                            value={formatSignedAmount(earnings.breakdown.priceChangeUsd)}
                            tone={signTone(earnings.breakdown.priceChangeUsd)}
                            note={`HODL ${displayMoney(earnings.breakdown.hodlOpenUsd)} → ${displayMoney(earnings.breakdown.hodlNowUsd)}`}
                          />
                          <EarningsRow
                            label="LP divergence vs Pair HODL"
                            value={formatSignedAmount(earnings.breakdown.impermanentLossUsd)}
                            tone={signTone(earnings.breakdown.impermanentLossUsd)}
                            note={`LP ${displayMoney(position.balanceUsd)} vs HODL ${displayMoney(earnings.breakdown.hodlNowUsd)}`}
                          />
                          <EarningsRow
                            label="Fees earned"
                            value={formatSignedAmount(earnings.breakdown.feesUsd)}
                            tone={signTone(earnings.breakdown.feesUsd)}
                            note={
                              earnings.breakdown.feesUsd === null
                                ? USD_VALUE_UNAVAILABLE
                                : undefined
                            }
                          />
                          {earnings.rewards.length > 0 && (
                            <EarningsRow
                              label="Rewards"
                              value={formatSignedAmount(earnings.breakdown.rewardsUsd)}
                              tone={signTone(earnings.breakdown.rewardsUsd)}
                              note={
                                earnings.breakdown.rewardsUsd === null
                                  ? USD_VALUE_UNAVAILABLE
                                  : undefined
                              }
                            />
                          )}
                          <EarningsGroup title="Opening" />
                          <EarningsRow
                            label="Entry swap fee"
                            value={formatSignedAmount(earnings.breakdown.entrySwapFeeUsd)}
                            tone={signTone(earnings.breakdown.entrySwapFeeUsd)}
                            note={`${entryRoute ? `via ${ROUTE_PROVIDER_NAMES[entryRoute]} ·` : `${displayPercent(earnings.breakdown.note.poolFeePercent)} of`} ${formatToken(earnings.breakdown.note.entrySwapInput?.decimal ?? null)} ${
                              earnings.breakdown.note.entrySwapInput
                                ? tokenName(position, earnings.breakdown.note.entrySwapInput.mint)
                                : ""
                            }`}
                          />
                          <EarningsRow
                            label="Entry price impact"
                            value={formatSignedAmount(earnings.breakdown.entryPriceImpactUsd)}
                            tone={signTone(earnings.breakdown.entryPriceImpactUsd)}
                            note={`${formatSignedPercent(earnings.breakdown.note.entryPriceImpactPercent)} vs quote mark`}
                          />
                          <EarningsRow
                            label="Entry network & priority"
                            value={formatSignedAmount(earnings.breakdown.entryNetworkFeeUsd)}
                            tone={signTone(earnings.breakdown.entryNetworkFeeUsd)}
                            note={`${formatToken(earnings.breakdown.note.entryNetworkFeeSol)} SOL`}
                          />
                          {signTone(earnings.breakdown.entryRentUsd) !== "" && (
                            <EarningsRow
                              label="Entry rent · not refundable"
                              value={formatSignedAmount(earnings.breakdown.entryRentUsd)}
                              tone={signTone(earnings.breakdown.entryRentUsd)}
                              note={`${formatToken(earnings.breakdown.note.entryRentSol)} SOL`}
                            />
                          )}
                          <EarningsRow
                            label="Held-aside price move"
                            value={formatSignedAmount(earnings.breakdown.heldAsidePriceMoveUsd)}
                            tone={signTone(earnings.breakdown.heldAsidePriceMoveUsd)}
                            note={`${formatToken(earnings.breakdown.note.heldAsideSol)} SOL · ${displayMoney(earnings.breakdown.note.solMarkAtOpening)} → ${displayMoney(earnings.breakdown.note.solMarkNow)}`}
                          />
                          <EarningsRow
                            label="Mark-to-market PnL"
                            value={formatSignedAmount(earnings.markToMarket.usd)}
                            tone={signTone(earnings.markToMarket.usd)}
                            note="before closing"
                            subtotal
                          />
                          <EarningsGroup title="Closing · from the live quote" />
                          <EarningsRow
                            label="Exit swap fee"
                            value={formatSignedAmount(earnings.breakdown.closing.exitSwapFeeUsd)}
                            tone={signTone(earnings.breakdown.closing.exitSwapFeeUsd)}
                            note={`${displayPercent(earnings.breakdown.note.poolFeePercent)} of ${formatToken(earnings.exitQuote.sellInput?.decimal ?? null)} ${earnings.exitQuote.sellInput ? tokenName(position, earnings.exitQuote.sellInput.mint) : ""}`}
                          />
                          <EarningsRow
                            label="Exit price impact"
                            value={formatSignedAmount(
                              earnings.breakdown.closing.exitPriceImpactUsd,
                            )}
                            tone={signTone(earnings.breakdown.closing.exitPriceImpactUsd)}
                            note={`${formatSignedPercent(earnings.breakdown.note.exitPriceImpactPercent)} · quote`}
                          />
                          <EarningsRow
                            label="Close network & priority"
                            value={formatSignedAmount(
                              earnings.breakdown.closing.closeNetworkFeeUsd,
                            )}
                            tone={signTone(earnings.breakdown.closing.closeNetworkFeeUsd)}
                            note={`${formatToken(earnings.breakdown.note.closeFeeSol)} SOL${
                              earnings.breakdown.note.closeTransactions === null
                                ? ""
                                : ` · ${String(earnings.breakdown.note.closeTransactions)} transaction${earnings.breakdown.note.closeTransactions === 1 ? "" : "s"}`
                            }`}
                          />
                          {earnings.exitQuote.unswapped.length > 0 && (
                            <EarningsRow
                              label="incl. Unswapped rewards"
                              value={earnings.exitQuote.unswapped
                                .map(
                                  (entry) =>
                                    `${entry.decimal === null ? "—" : formatToken(entry.decimal)} ${tokenName(position, entry.mint)}`,
                                )
                                .join(" · ")}
                              note={`already in Rewards · ${formatUsd(earnings.breakdown.closing.unswappedUsd)}`}
                              noteRow
                            />
                          )}
                          {signTone(earnings.breakdown.note.retainedRentSol) === "positive" && (
                            <EarningsRow
                              label="incl. Rent kept in token accounts"
                              value={`${formatToken(earnings.breakdown.note.retainedRentSol)} SOL`}
                              note={`already in Held aside · ${formatUsd(earnings.breakdown.closing.retainedRentUsd)}`}
                              noteRow
                            />
                          )}
                          {signTone(earnings.breakdown.note.closingRentSol) === "positive" && (
                            <EarningsRow
                              label="incl. Rent for new token accounts"
                              value={`${formatToken(earnings.breakdown.note.closingRentSol)} SOL`}
                              note={`stays in wallet · ${formatUsd(earnings.breakdown.closing.closingRentUsd)}`}
                              noteRow
                            />
                          )}
                          <EarningsRow
                            label="Exit assets (including retained rent)"
                            value={displayMoney(earnings.breakdown.closing.exitValueUsd)}
                            note={`${formatSolShort(earnings.exitValue.funding)} ${earnings.exitValue.mint ? tokenName(position, earnings.exitValue.mint) : ""} back in wallet${
                              earnings.exitValue.solBack !== null
                                ? ` · ${formatSolShort(earnings.exitValue.solBack)} SOL`
                                : ""
                            }`}
                          />
                          <EarningsRow
                            label="Asset result vs committed capital"
                            value={formatSignedAmount(earnings.breakdown.closing.exitPnlUsd)}
                            tone={signTone(earnings.breakdown.closing.exitPnlUsd)}
                            note={`${formatSignedPercent(earnings.exitPnl.percent)} · ${formatSignedSolShort(earnings.fundingResult.amount)} ${fundingSymbolValue ?? ""}${rewardsCaption(earnings)}${
                              earnings.fundingResult.solDelta !== null
                                ? ` · ${formatSignedSolShort(earnings.fundingResult.solDelta)} SOL`
                                : ""
                            }`}
                            total
                          />
                        </>
                      ) : (
                        <p className="earnings-unavailable">
                          — · {earnings.breakdown.reason ?? earnings.breakdown.closing.reason}
                        </p>
                      )}
                    </section>
                  </div>
                </div>
              );
            })()
          ) : (
            <>
              <div className="position-hero" aria-label="Active position summary">
                <section>
                  <span className="position-hero-label">Unclaimed fees USD</span>
                  <strong
                    className={`position-secondary-value num ${valueTone(position.pendingFeesUsd)}`}
                  >
                    {displayMoney(position.pendingFeesUsd)}
                  </strong>
                  {position.pendingFeesUsd === null && (
                    <small className="position-metric-reason">
                      Unclaimed fee valuation is not provided.
                    </small>
                  )}
                  <p>
                    Fees/day <span className="num">{displayMoney(position.feesPerDayUsd)}</span>
                    {position.feesPerDayUsd === null && <small> not provided</small>}
                    {" · "}APR <span className="num">{formatPercent2(position.aprPercent)}</span>
                    {position.aprPercent === null && <small> not provided</small>}
                  </p>
                </section>
                <section>
                  <span className="position-hero-label">LP value</span>
                  <strong className="position-secondary-value num">
                    {displayMoney(position.balanceUsd)}
                  </strong>
                  {position.balanceUsd === null && (
                    <small className="position-metric-reason">LP valuation is not provided.</small>
                  )}
                  {comparisonAvailable ? (
                    <p className="capital-assets-comparison num">
                      Capital {displayMoney(baselineCapital)} → total assets{" "}
                      {displayMoney(totalAssets)}
                    </p>
                  ) : (
                    <p className="capital-assets-comparison">
                      Capital → total assets <span className="num unknown">—</span>
                      <small>{comparisonReason}</small>
                    </p>
                  )}
                </section>
              </div>

              <div className="position-body">{rangeSection}</div>
            </>
          )}

          <details className="position-details">
            <summary>
              <span>Details</span>
              <svg viewBox="0 0 14 14" aria-hidden="true">
                <path d="m3.5 5.5 3.5 3.5 3.5-3.5" />
              </svg>
            </summary>
            <div className="position-audit">
              <section className="position-accounting" aria-label="Position accounting audit">
                <h3>Accounting audit</h3>
                <div className="position-metrics">
                  {metric(opening ? "Deposited" : "Balance", position.balanceUsd)}
                  <PositionPnl position={position} />
                  {metric(
                    "APR",
                    position.aprPercent,
                    "money-percent",
                    "Annualized only after 24 hours; not a forecast",
                  )}
                  {metric(
                    "Fees/day",
                    position.feesPerDayUsd,
                    "money",
                    "Average over the position lifetime after a full day",
                  )}
                  {metric("Pending", position.pendingFeesUsd)}
                </div>
                <dl className="position-audit-list">
                  <ResultRow
                    label="Rewards separate from fees"
                    value={displayMoney(position.rewardsUsd)}
                  />
                  <ResultRow
                    label="PnlBreakdown LP value"
                    value={displayMoney(breakdown?.lpUsd ?? null)}
                  />
                  <ResultRow
                    label="PnlBreakdown total assets"
                    value={displayMoney(totalAssets)}
                    reason={totalAssets === null ? auditReason : undefined}
                  />
                  <ResultRow
                    label="PnlBreakdown baseline capital"
                    value={displayMoney(baselineCapital)}
                  />
                  <ResultRow
                    label="PnlBreakdown net PnL"
                    value={displayMoney(breakdown?.netPnlUsd ?? null)}
                  />
                </dl>
              </section>
              <div className="position-configuration">
                {(() => {
                  const setup = position.openingSetup;
                  if (!setup)
                    return (
                      <section className="opening-unavailable" aria-label="Original opening setup">
                        <h3>Original opening setup</h3>
                        <p>— · Opening details are not provided</p>
                      </section>
                    );
                  // SETUP-frame source: `shown` is already re-expressed in the display frame,
                  // never a canonical conversion of setup values (the double-inversion bug).
                  const shown = frame.fromSetup(setup);
                  const setupRangeFrame = frame.rangeFrame(orientations);
                  const sameFrameAsSetup = setup.inverted === frame.inverted;
                  // A `RangeFrame` percentage is already signed (−x% below the reference); only the
                  // setup's unsigned `belowPercent` needs the lower edge's sign applied.
                  const lowerOffsetDisplay = setupRangeFrame
                    ? signedPercent(setupRangeFrame.requestedLowerPercent)
                    : sameFrameAsSetup
                      ? signedOffset(setup.belowPercent, "lower")
                      : "—";
                  const upperOffsetDisplay = setupRangeFrame
                    ? signedPercent(setupRangeFrame.requestedUpperPercent)
                    : sameFrameAsSetup
                      ? signedOffset(setup.abovePercent, "upper")
                      : "—";
                  return (
                    <section aria-label="Original opening setup">
                      <div className="configuration-heading">
                        <h3>Original opening setup</h3>
                        <span>Immutable</span>
                      </div>
                      <p>{frame.unit} · as entered at open</p>
                      <dl>
                        <div>
                          <dt>Reference</dt>
                          <dd>{formatPrice(shown.referencePrice, "long")}</dd>
                        </div>
                        <div>
                          <dt>Opening range</dt>
                          <dd>
                            {formatPrice(shown.lowerPrice, "long")} –{" "}
                            {formatPrice(shown.upperPrice, "long")}
                          </dd>
                        </div>
                        <div>
                          <dt>Width</dt>
                          {/* The width is a percentage of the setup's own quote; it is only
                              printed when that quote is the one shown. */}
                          <dd>{sameFrameAsSetup ? displayPercent(setup.widthPercent) : "—"}</dd>
                        </div>
                        <div>
                          <dt>Lower offset</dt>
                          <dd>{lowerOffsetDisplay}</dd>
                        </div>
                        <div>
                          <dt>Upper offset</dt>
                          <dd>{upperOffsetDisplay}</dd>
                        </div>
                        <div>
                          <dt>Initial policy</dt>
                          <dd>{policySummary(position, shown.initialExitPolicy)}</dd>
                        </div>
                      </dl>
                    </section>
                  );
                })()}
                <section aria-label="Current policy">
                  <div className="configuration-heading">
                    <h3>Current policy</h3>
                    <span>Editable</span>
                  </div>
                  <p>Price scale: {frame.unit}</p>
                  <dl>
                    <div>
                      <dt>Current LP range</dt>
                      <dd>
                        {formatPrice(displayBounds.lower, "long")} –{" "}
                        {formatPrice(displayBounds.upper, "long")}
                      </dd>
                    </div>
                    <div>
                      <dt>Automatic exit</dt>
                      <dd>{policySummary(position, displayPolicy, opening)}</dd>
                    </div>
                  </dl>
                </section>
              </div>
              <section
                className="position-monitor-audit"
                aria-label="Monitoring and valuation audit"
              >
                <h3>Monitoring and valuation</h3>
                <dl className="position-audit-list">
                  <ResultRow label="Saved policy" value={policyText} />
                  <ResultRow label="Position monitor state" value={position.monitorState || "—"} />
                  <ResultRow label="Runtime monitor" value={monitor.text} />
                  <ResultRow
                    label="Overall monitoring"
                    value={detail ? monitorReasons[detail.reason] : "—"}
                    reason={!detail ? "No active runtime monitor detail" : undefined}
                  />
                  <ResultRow
                    label="Source"
                    value={detail ? detail.source : "—"}
                    reason={!detail ? "No active runtime monitor detail" : undefined}
                  />
                  <ResultRow
                    label="Oldest active-pool observation"
                    value={detail?.lastObservedAt ? formatAgo(detail.lastObservedAt) : "—"}
                    reason={!detail?.lastObservedAt ? "Observation time not provided" : undefined}
                  />
                  <ResultRow
                    label="Trigger"
                    value={detail ? (detail.triggerReason ?? "None") : "—"}
                  />
                  <ResultRow
                    label="Next retry"
                    value={
                      detail?.nextRetryAt
                        ? formatWhen(detail.nextRetryAt)
                        : detail
                          ? "Not scheduled"
                          : "—"
                    }
                  />
                  <ResultRow
                    label="Next reconnect"
                    value={
                      detail?.nextReconnectAt
                        ? formatWhen(detail.nextReconnectAt)
                        : detail
                          ? "Not scheduled"
                          : "—"
                    }
                  />
                  <ResultRow
                    label="Market observed"
                    value={position.observedAt ? formatAgo(position.observedAt) : "—"}
                    reason={!position.observedAt ? "Observation time not provided" : undefined}
                  />
                  <ResultRow
                    label="Valuation note"
                    value={position.valuationNote ?? "—"}
                    reason={!position.valuationNote ? "Valuation note not provided" : undefined}
                  />
                </dl>
              </section>
            </div>
          </details>

          {!position.operation || terminalOperation ? (
            <footer className="position-footer">
              <span>
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M8 1.5 13.5 4v4c0 3.2-2.3 5.6-5.5 6.5C4.8 13.6 2.5 11.2 2.5 8V4Z" />
                  <path d="m5.5 8 1.8 1.8L10.8 6.3" />
                </svg>
                {compactPolicySummary(
                  position,
                  displayPolicy,
                  opening,
                  hasSavedStops,
                  autopilotOwned,
                )}
                {autopilotOwned && !opening && policy.enabled && (
                  <span className="tag locked">locked</span>
                )}
              </span>
              <span>
                {detail ? monitorReasons[detail.reason] : `Monitor ${monitor.text}`} · price seen{" "}
                {position.observedAt ? formatAgo(position.observedAt) : "—"}
              </span>
            </footer>
          ) : null}
          {position.operation && !opening && !closeRunning && (
            <OperationStatus operation={position.operation} />
          )}
        </div>
      )}
    </article>
  );
}
